/**
 * API Keys Page
 *
 * Allows users to create and manage their API keys for external API access.
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { apiKeyService, ApiKeyInfo } from '../services/api';
import { CreateApiKeyModal } from '../components/api-keys/CreateApiKeyModal';

export const ApiKeysPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deletingKeyId, setDeletingKeyId] = useState<string | null>(null);

  // Fetch API keys
  const { data, isLoading, error } = useQuery({
    queryKey: ['api-keys'],
    queryFn: () => apiKeyService.list(),
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (keyId: string) => apiKeyService.delete(keyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] });
      toast.success('API key deleted');
      setDeletingKeyId(null);
    },
    onError: (error: Error) => {
      toast.error(error.message);
      setDeletingKeyId(null);
    },
  });

  const handleDelete = (keyId: string) => {
    if (window.confirm('Are you sure you want to delete this API key? This action cannot be undone.')) {
      setDeletingKeyId(keyId);
      deleteMutation.mutate(keyId);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-32 mb-8"></div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="h-4 bg-gray-200 rounded w-full mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            Failed to load API keys. Please try again.
          </div>
        </div>
      </div>
    );
  }

  const keys = data?.keys || [];

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">API Keys</h1>
            <p className="text-gray-600 mt-1">
              Manage your API keys for external API access
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create New Key
          </button>
        </div>

        {/* MCP Server Info */}
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
          <h3 className="font-medium text-purple-900 mb-2 flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            MCP Server (Claude, ChatGPT, etc.)
          </h3>
          <p className="text-sm text-purple-700 mb-3">
            Connect AI assistants like Claude Desktop, Claude Web, or ChatGPT to run browser automations.
          </p>
          <div className="space-y-2">
            <div>
              <span className="text-xs font-medium text-purple-600 uppercase">MCP Server URL</span>
              <div className="flex items-center gap-2 mt-1">
                <code className="text-sm text-purple-800 bg-purple-100 px-3 py-1.5 rounded flex-1">
                  https://api.aitinyfish.com/mcp
                </code>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText('https://api.aitinyfish.com/mcp');
                    toast.success('Copied to clipboard');
                  }}
                  className="p-1.5 text-purple-600 hover:bg-purple-100 rounded transition-colors"
                  title="Copy URL"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
            </div>
            <p className="text-xs text-purple-600 mt-2">
              Supports OAuth (sign in with Google) or API key authentication via <code className="bg-purple-100 px-1 rounded">X-API-Key</code> header.
            </p>
          </div>
        </div>

        {/* Direct API Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h3 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
            </svg>
            Direct API Access
          </h3>
          <p className="text-sm text-blue-700 mb-3">
            Integrate browser automation directly into your applications via REST API.
          </p>
          <div className="space-y-2">
            <div>
              <span className="text-xs font-medium text-blue-600 uppercase">Endpoint</span>
              <div className="flex items-center gap-2 mt-1">
                <code className="text-sm text-blue-800 bg-blue-100 px-3 py-1.5 rounded flex-1">
                  POST https://api.aitinyfish.com/api/v1/automation/run-sse
                </code>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText('https://api.aitinyfish.com/api/v1/automation/run-sse');
                    toast.success('Copied to clipboard');
                  }}
                  className="p-1.5 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                  title="Copy URL"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
            </div>
            <p className="text-xs text-blue-600 mt-2">
              Authenticate with <code className="bg-blue-100 px-1 rounded">X-API-Key: sk-evb-...</code> header. Returns SSE stream.
            </p>
          </div>
        </div>

        {/* Keys List */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {keys.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <svg
                className="w-12 h-12 mx-auto text-gray-400 mb-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                />
              </svg>
              <p className="font-medium">No API keys yet</p>
              <p className="text-sm mt-1">Create your first API key to get started</p>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Key
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Used
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {keys.map((key: ApiKeyInfo) => (
                  <tr key={key.key_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="font-medium text-gray-900">{key.name}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <code className="text-sm text-gray-600 bg-gray-100 px-2 py-1 rounded">
                        {key.key_prefix}...{key.key_suffix}
                      </code>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(key.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {key.last_used_at ? formatDate(key.last_used_at) : 'Never'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={() => handleDelete(key.key_id)}
                        disabled={deletingKeyId === key.key_id}
                        className="text-red-600 hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
                      >
                        {deletingKeyId === key.key_id ? 'Deleting...' : 'Delete'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Create Modal */}
        {showCreateModal && (
          <CreateApiKeyModal
            onClose={() => setShowCreateModal(false)}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: ['api-keys'] });
            }}
          />
        )}
      </div>
    </div>
  );
};
